import type { TimelyPresentableAiPrediction } from '@timelyapp/integration-cloud-api-spec';
import isEqual from 'lodash-es/isEqual';
import { mutateDateTimeRange } from '../time-and-date-ranges/mutate-datetime-range';
import { sumDurationInDateRange } from '../time-and-date-ranges/seconds-in-daterange';

export type ConsolidationParams = {
  /** Predictions with the same `id`, or `project_id` and identical `label_ids` will be consolidated. */
  matchOn?: 'id' | 'compatibility';
};
/**
 * Merges fractured or ~identical AI predictions based on the specified `matchOn` parameter
 *
 * @param matchOn - An optional parameter that specifies how to match predictions for consolidation. It can have two values:
 *   - 'id': Predictions with the same `id` will be consolidated.
 *     Example: If there are two predictions with the same `id`, their `total_seconds` and `timestamps` will be combined into a single prediction.
 *   - 'compatibility' (default): Predictions with the same `id`, or identical `project_id` & `label_ids` will be consolidated.
 *     Example: If there are two predictions with the same `project_id` and the same set of `label_ids`, their `total_seconds` and `timestamps` will be combined into a single prediction.
 */
export const consolidatePredictions = (
  ai_predictions: ReadonlyArray<TimelyPresentableAiPrediction>,
  { matchOn = 'compatibility' }: ConsolidationParams = {}
): TimelyPresentableAiPrediction[] => {
  const consolidatedIntervals: TimelyPresentableAiPrediction[] = [];

  for (const prediction of ai_predictions) {
    const existingPrediction = consolidatedIntervals.find(p =>
      matchOn === 'id' ? p.id === prediction.id : areCompatible(p, prediction)
    );

    if (existingPrediction) {
      // Add timestamps together, avoiding overlap.
      const timestamps = mutateDateTimeRange(
        'add',
        prediction.timestamps,
        existingPrediction.timestamps
      );

      // Calculate total duration off timestamps
      existingPrediction.timestamps = timestamps;
      existingPrediction.total_seconds = sumDurationInDateRange(existingPrediction.timestamps);
    } else {
      consolidatedIntervals.push({ ...prediction });
    }
  }

  return consolidatedIntervals;
};

const areCompatible = (
  a: DeepReadonly<TimelyPresentableAiPrediction>,
  b: DeepReadonly<TimelyPresentableAiPrediction>
): boolean => {
  // always consider compatible if prediction has a hard link to existing hour id.
  if (!!a.existing_hour_id && a.existing_hour_id === b.existing_hour_id) return true;

  // Check if project IDs match
  if (a.project_id !== b.project_id) return false;

  // If this is a split prediction, always support merging
  if (a.id === b.id) return true;

  const matchesProject = a.project_id === b.project_id;
  const matchesLabels = isEqual(a.label_ids.slice().sort(), b.label_ids.slice().sort());

  return matchesProject && matchesLabels;
};

type DeepReadonly<T> = {
  readonly [P in keyof T]: DeepReadonly<T[P]>;
};
